"use strict";

const OrderrItemGroupShape = require("./OrderrItemGroupShape");
const WallClock = require("./WallClock");

/**
 * MOB-516 — which PointSell serves which part of an orderr.
 *
 * An orderr has a rental part and a school part. The rental part is bound to ONE PointSell,
 * the header `orderr.pointSell`, because the rental stock is booked at that branch. Each
 * school item group carries its own `pointSell`, the one that owns its PointMeeting. The
 * 5 questions below are asked by the layer (`MobOrderr` at addToCart, the restore sync) and by
 * the storefront (the rent-disabled guard, the checkout page), so the rules live here once,
 * next to `OrderrItemGroupShape`. The storefront reaches it through the adapter
 * `src/libs/PointSellLock.js` (`layerCjsToEsm` in `vite.config.js`) — see ADR 0001 and 0002.
 *
 * The lock is a function of the item groups, not a stored flag: a school-only cart gives null
 * even when the header is filled, which it is for every orderr written before wave 2.
 *
 * Comparisons are on `mssId`, never on `id`. An `id` is bound to a `mobVersion`
 * (`MssToMobTransform`), and a DRAFT can live across an init.
 */
class PointSellLock {
  /**
   * True when any item group has a RENTAL product. Rent and course item groups are told apart
   * by `product.productType`, the same way `Cart.vue`, `CheckoutPickUp.vue` and
   * `CheckoutCompleted.vue` split the cart for display.
   */
  static hasRentalItemGroup(orderr) {
    return PointSellLock.itemGroupsOf(orderr).some(PointSellLock.isRentalGroup);
  }

  /**
   * The PointSell that locks the rental part: the header PointSell when a RENTAL group exists,
   * else null.
   */
  static rentalPointSellLock(orderr) {
    if (orderr == null || orderr.pointSell == null) {
      return null;
    }
    return PointSellLock.hasRentalItemGroup(orderr) ? orderr.pointSell : null;
  }

  /** The same PointSell: equal `mssId`. False when either side is missing. */
  static isSamePointSell(a, b) {
    return a != null && b != null && a.mssId != null && a.mssId === b.mssId;
  }

  /**
   * The one PointSell id that every input item of a RENTAL group names through
   * `article.pointSellId`, or null: no items, an item with no PointSell, or 2 different
   * PointSells (a set whose members come from 2 shops). A set or a packet is 1 item group, and
   * a correct client builds each member article from the same `state.pointSell`, so a null
   * here is a broken input, not a choice — `addToCart` refuses it with `ERROR_105`.
   */
  static rentalPointSellIdOfItems(orderrItems) {
    if (!Array.isArray(orderrItems) || orderrItems.length === 0) {
      return null;
    }
    const ids = new Set();
    for (const item of orderrItems) {
      const pointSellId = item == null || item.article == null ? null : item.article.pointSellId;
      if (pointSellId == null) {
        return null;
      }
      ids.add(pointSellId);
    }
    return ids.size === 1 ? ids.values().next().value : null;
  }

  /**
   * The PointSell that serves one item group, in 3 steps:
   *   1. the group's own PointSell,
   *   2. the header PointSell of the orderr,
   *   3. the first PointSell of the location of the group's PointMeeting, by `orderNumber`
   *      (the same fallback `CoursePrivateBookingV2.defaultPointMeeting` takes).
   * When all 3 miss the result is null and `FEAT16_POINTSELL_FALLBACK` goes to `console.error`
   * — a fixed string, so `cloudwatch-logs.js search` counts the cases. The stored field stays
   * null in the fallback cases on purpose, so they stay countable.
   */
  static itemGroupPointSell(orderrItemGroup, orderr) {
    if (orderrItemGroup != null && orderrItemGroup.pointSell != null) {
      return orderrItemGroup.pointSell;
    }
    if (orderr != null && orderr.pointSell != null) {
      return orderr.pointSell;
    }

    const pointMeeting = orderrItemGroup == null ? null : orderrItemGroup.pointMeeting;
    const location = pointMeeting == null ? null : pointMeeting.location;
    const locationPointSells = PointSellLock.itemsOf(
      location == null ? null : location.pointSells
    );
    const byOrderNumber = locationPointSells
      .filter((ps) => ps != null)
      .sort((a, b) => (a.orderNumber || 0) - (b.orderNumber || 0));
    if (byOrderNumber.length > 0) {
      return byOrderNumber[0];
    }

    console.error("FEAT16_POINTSELL_FALLBACK", {
      orderrId: orderr == null ? null : orderr.id,
      orderrItemGroupId: orderrItemGroup == null ? null : orderrItemGroup.id,
      pointMeetingMssId: pointMeeting == null ? null : pointMeeting.mssId,
    });
    return null;
  }

  /**
   * The distinct PointSells of the orderr, in the order of the checkout page: the rental
   * PointSell first, then the school PointSells by the first `dateStart` of their item groups.
   * A `dateStart` is wall-clock time at the PointSell, so the order uses its digits and ignores
   * its offset (`WallClock.sortKey`, MOB-058).
   * A group whose PointSell resolves to null is skipped (the marker is already written).
   */
  static orderrPointSells(orderr) {
    return PointSellLock.orderrPointSellResolution(orderr).pointSells;
  }

  /**
   * `pointSells` = `orderrPointSells`, and `groupPointSells` = the resolved PointSell of each
   * item group, in group order, null where the resolver gave null. The list alone does not
   * show a group that resolved to null, or a second object of 1 `mssId` (a DRAFT across an
   * init). Each group is resolved once, so a caller that needs both writes
   * `FEAT16_POINTSELL_FALLBACK` once per group, not twice (MOB-241).
   */
  static orderrPointSellResolution(orderr) {
    const groups = PointSellLock.itemGroupsOf(orderr);
    const groupPointSells = [];

    // The rental slot is the lock (the header). Only when the header is missing does the
    // resolved PointSell of a RENTAL group take the slot — the same value in every consistent
    // orderr, because `itemGroupPointSell` falls back to the header at step 2.
    const lock = PointSellLock.rentalPointSellLock(orderr);
    const rentalKeys = new Set(lock == null ? [] : [PointSellLock.keyOf(lock)]);

    // key -> { pointSell, firstDate }
    const byKey = new Map();
    for (const group of groups) {
      const pointSell = PointSellLock.itemGroupPointSell(group, orderr);
      groupPointSells.push(pointSell);
      if (pointSell == null) {
        continue;
      }
      const key = PointSellLock.keyOf(pointSell);
      if (lock == null && PointSellLock.isRentalGroup(group)) {
        rentalKeys.add(key);
      }
      const date = WallClock.sortKey(group.dateStart);
      const entry = byKey.get(key);
      if (entry == null) {
        byKey.set(key, { pointSell, firstDate: date });
      } else if (date != null && (entry.firstDate == null || date < entry.firstDate)) {
        entry.firstDate = date;
      }
    }

    const entries = Array.from(byKey.values());
    const rental = entries.filter((entry) => rentalKeys.has(PointSellLock.keyOf(entry.pointSell)));
    const school = entries
      .filter((entry) => !rentalKeys.has(PointSellLock.keyOf(entry.pointSell)))
      .sort((a, b) => {
        if (a.firstDate === b.firstDate) return 0;
        if (a.firstDate == null) return 1;
        if (b.firstDate == null) return -1;
        return a.firstDate - b.firstDate;
      });
    return {
      pointSells: rental.concat(school).map((entry) => entry.pointSell),
      groupPointSells,
    };
  }

  /**
   * The header PointSell that the MSS export sends in `orderr_m2.id_point_sell`: the rental
   * lock, or for a school-only orderr the PointSell of the oldest item group by `createdAt`.
   * MSS needs 1 PointSell on the orderr, but MOB keeps the header for the rental shop only, so
   * the export calculates this value and MOB does not store it. A group whose PointSell
   * resolves to null is skipped. Null when no group resolves.
   */
  static exportHeaderPointSell(orderr) {
    const lock = PointSellLock.rentalPointSellLock(orderr);
    if (lock != null) {
      return lock;
    }
    const oldestFirst = PointSellLock.itemGroupsOf(orderr)
      .filter((group) => group != null)
      .sort((a, b) => {
        const timeA = PointSellLock.timeOf(a.createdAt);
        const timeB = PointSellLock.timeOf(b.createdAt);
        if (timeA === timeB) return 0;
        if (timeA == null) return 1;
        if (timeB == null) return -1;
        return timeA - timeB;
      });
    for (const group of oldestFirst) {
      const pointSell = PointSellLock.itemGroupPointSell(group, orderr);
      if (pointSell != null) {
        return pointSell;
      }
    }
    return null;
  }

  /** The identity of a PointSell: `mssId`, with `id` as the last resort for a partial object. */
  static keyOf(pointSell) {
    return pointSell.mssId != null ? "mss:" + pointSell.mssId : "id:" + pointSell.id;
  }

  /**
   * An AWSDateTime string or a Date as epoch milliseconds, null when absent or unreadable. For an
   * instant only, such as `createdAt`. A wall-clock time such as `dateStart` orders by
   * `WallClock.sortKey` (MOB-058).
   */
  static timeOf(value) {
    if (value == null) {
      return null;
    }
    const time = value instanceof Date ? value.getTime() : Date.parse(String(value));
    return isNaN(time) ? null : time;
  }

  static isRentalGroup(orderrItemGroup) {
    return (
      orderrItemGroup != null &&
      orderrItemGroup.product != null &&
      orderrItemGroup.product.productType === "RENTAL"
    );
  }

  /**
   * The item groups of the orderr as a plain array, whether they arrived as a GraphQL
   * connection (`{ items: [...] }`, read from the API) or as a plain array (built in memory).
   * Never null, so the callers can iterate.
   */
  static itemGroupsOf(orderr) {
    return PointSellLock.itemsOf(orderr == null ? null : orderr.orderrItemGroups);
  }

  static itemsOf(connectionOrArray) {
    const items = OrderrItemGroupShape.itemsOf({ orderrItems: connectionOrArray });
    return Array.isArray(items) ? items : [];
  }
}

module.exports = PointSellLock;
